/**
 * JobsActions Component
 *
 * Bulk action toolbar for Jobs table showing:
 * - Rerun Jobs (R key)
 * - Cancel Jobs (C key)
 *
 * Follows the pattern used by AssetActions, VulnerabilityActions, SeedsActions
 * for platform consistency.
 */

import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Button } from '@/components/Button';
import {
  type ActionConfigUnion,
  TableActionsBar,
} from '@/components/table/actions';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useJobsActionHandlers } from '@/sections/jobs/hooks';
import {
  canCancelJob,
  canRerunJob,
} from '@/sections/jobs/utils/jobStatus.util';
import { Job } from '@/types';

import { JobsScanStatus } from './JobsScanStatus';

export interface JobsActionsProps {
  /** Jobs to perform actions on */
  jobs: Job[];

  /** Number of selected rows */
  selectedRowsCount: number;

  /** Callback to clear selection after successful action */
  onClearSelection: () => void;

  /** Refetch jobs data after mutations */
  refetch: () => void;

  /** Disable all actions */
  disabled?: boolean;

  /** Hide the "Actions:" label */
  hideLabel?: boolean;

  /** Hide action buttons */
  hideActions?: boolean;

  /** Additional CSS classes */
  className?: string;

  /** Next run text for scan status tooltip */
  nextRunText?: string;

  /** Hide scan status control (for modal contexts where account-wide control is not relevant) */
  hideScanStatus?: boolean;

  /** Optional email for checking frozen status of specific customer (for impersonation) */
  email?: string;
}

/**
 * Jobs bulk action component
 *
 * Provides Rerun and Cancel bulk operations for selected jobs.
 * Integrates with TableActionsBar for consistent platform styling.
 *
 * @param props - JobsActions props
 * @returns Action bar component with job-specific operations
 */
export function JobsActions({
  jobs,
  selectedRowsCount: _selectedRowsCount,
  onClearSelection,
  refetch,
  disabled = false,
  hideLabel,
  hideActions,
  className,
  nextRunText = '',
  hideScanStatus = false,
  email,
}: JobsActionsProps) {
  const { canWriteJobs } = useEntitlements();

  // Get bulk action handlers from hook
  const handlers = useJobsActionHandlers({
    selectedJobs: jobs,
    refetch,
  });

  // Filter jobs by eligibility for each action
  // Only jobs in terminal states (Pass/Fail) can be rerun
  // Only jobs in active states (Running/Queued) can be canceled
  const eligibleForRerun = jobs.filter(canRerunJob);
  const eligibleForCancel = jobs.filter(canCancelJob);

  // Build actions following Asset table pattern
  // Global scan control first (if shown), then selection-dependent actions
  const actions: ActionConfigUnion[] = [
    // Global scan pause/resume control - rendered first as it's account-wide
    // Hidden in modal contexts or when user lacks write_jobs entitlement
    ...(hideScanStatus || !canWriteJobs
      ? []
      : [
          {
            type: 'Custom' as const,
            position: 'first' as const,
            testId: 'jobs-scan-status',
            render: () => (
              <JobsScanStatus
                refetch={refetch}
                nextRunText={nextRunText}
                email={email}
              />
            ),
          },
        ]),
    // Selection-dependent actions (Rerun/Cancel) - gated by write_jobs entitlement
    ...(canWriteJobs
      ? [
          {
            type: 'Change Status' as const,
            render: () => (
              <div className="flex items-center gap-3">
                <Button
                  data-testid="jobs-rerun-button"
                  onClick={async () => {
                    await handlers.handleRerun(eligibleForRerun);
                    onClearSelection();
                  }}
                  disabled={disabled || eligibleForRerun.length === 0}
                  styleType="rounded"
                  size="xxs"
                  startIcon={<ArrowPathIcon className="size-4" />}
                  aria-label="Rerun jobs"
                >
                  Rerun
                </Button>
                <Button
                  data-testid="jobs-cancel-button"
                  onClick={async () => {
                    await handlers.handleCancel(eligibleForCancel);
                    onClearSelection();
                  }}
                  disabled={disabled || eligibleForCancel.length === 0}
                  styleType="rounded"
                  size="xxs"
                  startIcon={<XMarkIcon className="size-4" />}
                  aria-label="Cancel jobs"
                >
                  Cancel
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <TableActionsBar
      selectedItems={jobs}
      actions={actions}
      disabled={disabled}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="jobs"
    />
  );
}
